class ProductMediaGallery extends HTMLElement {
  constructor() {
    super();
    this.lightbox = this.querySelector('.product-gallery-lightbox');
    this.lightboxClose = this.querySelector('.product-gallery-lightbox__close');
    this.lightboxOverlay = this.querySelector('.product-gallery-lightbox__overlay');
    this.mobileSwiper = null;
    this.lightboxSwiper = null;
    this.swiperReady = false;
  }

  connectedCallback() {
    this.waitForSwiper().then(() => {
      this.swiperReady = true;
      this.initMobileSwiper();
      this.initDesktopClicks();
      this.initLightbox();
      this.initStickyBehavior();
    });
  }

  waitForSwiper() {
    return new Promise((resolve) => {
      if (typeof Swiper !== 'undefined') {
        resolve();
        return;
      }
      const check = setInterval(() => {
        if (typeof Swiper !== 'undefined') {
          clearInterval(check);
          resolve();
        }
      }, 50);
    });
  }

  initMobileSwiper() {
    const mobileContainer = this.querySelector('.product-gallery-swiper');
    if (!mobileContainer) return;

    this.mobileSwiper = new Swiper(mobileContainer, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      pagination: {
        el: this.querySelector('.product-gallery-pagination'),
        clickable: true,
      },
    });

    // Thumbnail navigation
    const thumbs = this.querySelectorAll('.product-gallery-thumb');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbIndex, 10) || 0;
        if (this.mobileSwiper) this.mobileSwiper.slideTo(index);
      });
    });

    // Update active thumbnail on slide change
    if (thumbs.length > 0) {
      this.mobileSwiper.on('slideChange', () => {
        const activeIndex = this.mobileSwiper.activeIndex;
        thumbs.forEach((t, i) => {
          t.classList.toggle('is-active', i === activeIndex);
        });
      });
    }
  }

  initDesktopClicks() {
    const items = this.querySelectorAll('.product-gallery-desktop .product-gallery__item');
    items.forEach((item) => {
      item.style.cursor = 'zoom-in';
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(item.dataset.index, 10) || 0;
        this.openLightbox(index);
      });
    });

    // Mobile images also open lightbox on tap
    const mobileSlides = this.querySelectorAll('.product-gallery-mobile .swiper-slide');
    mobileSlides.forEach((slide) => {
      slide.addEventListener('click', (e) => {
        // Don't open lightbox if user is swiping
        if (this.mobileSwiper && this.mobileSwiper.animating) return;
        const index = parseInt(slide.dataset.index, 10) || 0;
        this.openLightbox(index);
      });
    });
  }

  initLightbox() {
    if (!this.lightbox) return;

    // Close button
    if (this.lightboxClose) {
      this.lightboxClose.addEventListener('click', () => this.closeLightbox());
    }

    // Close on overlay click
    if (this.lightboxOverlay) {
      this.lightboxOverlay.addEventListener('click', () => this.closeLightbox());
    }

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.lightbox.classList.contains('is-open')) {
        this.closeLightbox();
      }
    });
  }

  openLightbox(startIndex = 0) {
    if (!this.lightbox) return;

    this.lightbox.classList.add('is-open');
    this.lightbox.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';

    // Init or update lightbox swiper
    if (!this.lightboxSwiper) {
      const swiperContainer = this.lightbox.querySelector('.product-gallery-lightbox__swiper');
      this.lightboxSwiper = new Swiper(swiperContainer, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: false,
        initialSlide: startIndex,
        navigation: {
          nextEl: this.lightbox.querySelector('.swiper-button-next'),
          prevEl: this.lightbox.querySelector('.swiper-button-prev'),
        },
        pagination: {
          el: this.lightbox.querySelector('.product-gallery-lightbox__pagination'),
          clickable: true,
        },
        zoom: {
          maxRatio: 3,
          minRatio: 1,
        },
      });
    } else {
      this.lightboxSwiper.slideTo(startIndex, 0);
    }
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove('is-open');
    this.lightbox.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    // Reset zoom if active
    if (this.lightboxSwiper && this.lightboxSwiper.zoom) {
      this.lightboxSwiper.zoom.out();
    }
  }

  initStickyBehavior() {
    // Gallery should not be sticky — the info column handles sticky behavior
    // Remove any sticky class that may have been set
    this.style.position = '';
    this.style.top = '';
  }

  disconnectedCallback() {
    if (this.mobileSwiper) {
      this.mobileSwiper.destroy(true, true);
    }
    if (this.lightboxSwiper) {
      this.lightboxSwiper.destroy(true, true);
    }
  }
}

customElements.define('product-media-gallery', ProductMediaGallery);
